import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, BookOpenCheck, Clock, Loader2, Trophy, TrendingDown } from "lucide-react";
import PracticeConfig from "@/components/test-section/PracticeConfig";
import { MODE_META, SECTION_ACCENT } from "@/components/test-section/meta";

function SectionTabs({ sections, active, onPick }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="ts-section-tabs">
      {sections.map((s) => {
        const on = s.id === active;
        return (
          <button key={s.id} onClick={() => onPick(s.id)}
            className="text-left rounded-2xl p-4 transition-transform hover:-translate-y-0.5"
            style={{ background: on ? SECTION_ACCENT[s.id] : "var(--paper)", color: on ? "white" : "var(--text)", border: `1.5px solid ${on ? SECTION_ACCENT[s.id] : "var(--border)"}` }}
            data-testid={`ts-section-tab-${s.id}`}>
            <div className="font-display text-2xl font-bold">{s.short}</div>
            <div className="text-xs mt-1 leading-snug" style={{ opacity: on ? 0.92 : 0.7 }}>{s.name}</div>
          </button>
        );
      })}
    </div>
  );
}

function ModeGrid({ modes, active, onPick }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="ts-mode-grid">
      {modes.map((m, i) => {
        const Meta = MODE_META[m.id];
        const Icon = Meta.icon;
        const on = m.id === active;
        return (
          <button key={m.id} onClick={() => onPick(m.id)}
            className="pm-card text-left p-4 flex gap-3 transition-transform hover:-translate-y-0.5"
            style={{ borderColor: on ? "var(--primary)" : undefined, boxShadow: on ? "0 0 0 2px var(--primary)" : undefined }}
            data-testid={`ts-mode-${m.id}`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: on ? "var(--primary)" : "var(--muted)" }}>
              <Icon size={18} color={on ? "white" : "var(--primary)"} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>MODE {i + 1}</div>
              <div className="font-display font-bold">{m.name}</div>
              <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{Meta.desc}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Insights({ sectionId }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/test-section/insights/${sectionId}`).then((r) => setData(r.data)).catch(() => setData({ topics: [], skills: [] })); }, [sectionId]);
  if (!data) return null;
  const groups = [["Learning objectives", (data.objectives || []).slice(0, 3)], ["Reasoning skills", data.skills.slice(0, 3)], ["Topics", data.topics.slice(0, 3)]].filter(([, g]) => g.length);
  const rows = groups.flatMap(([, g]) => g);
  return (
    <div className="pm-card p-5" data-testid="ts-insights">
      <div className="flex items-center gap-2 font-display font-bold mb-3"><TrendingDown size={16} /> Where you lose points</div>
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Finish a practice set and your weakest skills and topics will show up here.</p>
      ) : groups.map(([label, g]) => (
        <div key={label} className="mb-3" data-testid={`ts-insight-group-${label.split(" ")[0].toLowerCase()}`}>
          <div className="text-[10px] uppercase tracking-widest font-semibold mb-1.5" style={{ color: "var(--text-muted)" }}>{label}</div>
          {g.map((r) => (
        <div key={r.id} className="mb-2.5" data-testid={`ts-insight-${String(r.id).slice(0, 40).replace(/\W+/g, "-")}`}>
          <div className="flex justify-between text-xs mb-1"><span className="truncate pr-2">{r.name}</span><span className="font-mono">{Math.round(r.accuracy * 100)}%</span></div>
          <div className="h-1.5 rounded-full" style={{ background: "var(--muted)" }}>
            <div className="h-1.5 rounded-full" style={{ width: `${Math.max(4, r.accuracy * 100)}%`, background: r.accuracy < 0.6 ? "var(--weakness)" : "var(--primary)" }} />
          </div>
        </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function History({ sectionId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/test-section/history", { params: { section_id: sectionId } }).then((r) => setRows(r.data.sessions || [])).catch(() => {}); }, [sectionId]);
  if (!rows.length) return null;
  return (
    <div className="pm-card p-5" data-testid="ts-history">
      <div className="flex items-center gap-2 font-display font-bold mb-3"><Clock size={16} /> Recent sessions</div>
      {rows.slice(0, 6).map((s) => (
        <Link key={s.id} to={`/app/practice-test/session/${s.id}`} className="flex items-center justify-between text-sm py-2 border-b last:border-0 hover:opacity-75" style={{ borderColor: "var(--border)" }} data-testid={`ts-history-${s.id}`}>
          <span>{s.mode_name}</span>
          <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
            {s.status === "finished" ? `${s.results?.correct}/${s.results?.total}` : "in progress"}
          </span>
        </Link>
      ))}
    </div>
  );
}

function CarsPanel({ cars }) {
  return (
    <div className="pm-card p-8" data-testid="ts-cars-panel">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: SECTION_ACCENT.cars }}><BookOpen size={20} color="white" /></div>
      <h2 className="font-display text-2xl font-bold mb-2">CARS runs on CARS Academy</h2>
      <p className="text-sm mb-6 max-w-xl" style={{ color: "var(--text-secondary)" }}>
        Every CARS practice mode, the reading tutor, error log and the 53-question / 90-minute CARS simulation live in CARS Academy.
        The Full-Length MCAT Simulation below pulls its CARS section straight from the same {cars.passages}-passage CARS Academy bank.
      </p>
      <Link to="/app/cars" className="pm-btn-primary inline-flex items-center gap-2" data-testid="ts-open-cars-academy">Open CARS Academy <ArrowRight size={14} /></Link>
    </div>
  );
}

function FullLengthCard() {
  return (
    <Link to="/app/practice-test/full" className="mt-10 rounded-3xl p-7 flex items-center gap-5 flex-wrap transition-transform hover:-translate-y-0.5"
      style={{ background: "var(--text)", color: "var(--paper)" }} data-testid="ts-full-length-card">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "var(--accent)" }}><Trophy size={24} color="white" /></div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] font-mono opacity-70">MODE 8</div>
        <div className="font-display text-2xl font-bold">Full-Length MCAT Simulation</div>
        <div className="text-sm opacity-80 mt-1">C/P 59Q · 95 min — CARS 53Q · 90 min — B/B 59Q · 95 min — P/S 59Q · 95 min. Estimated 472–528 score.</div>
      </div>
      <ArrowRight size={22} />
    </Link>
  );
}

function readError(e) {
  const detail = e?.response?.data?.detail;
  if (e?.response?.status === 402) return { paywall: true, message: detail?.reason || "You're out of credits." };
  return { paywall: false, message: typeof detail === "string" ? detail : "Couldn't start that session. Try again." };
}

export default function TestSection() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("cp");
  const [mode, setMode] = useState("topic");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { api.get("/test-section/overview").then((r) => setData(r.data)); }, []);

  const start = async (body) => {
    setBusy(true); setError(null);
    try {
      const r = await api.post("/test-section/sessions", body, { silent: true });
      window.dispatchEvent(new Event("pm-billing-refresh"));
      nav(`/app/practice-test/session/${r.data.id}`);
    } catch (e) { setError(readError(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="py-24 text-center" data-testid="ts-loading"><Loader2 className="animate-spin mx-auto" /></div>;
  const tabs = [data.sections[0], { id: "cars", ...data.cars }, data.sections[1], data.sections[2]];
  const section = data.sections.find((s) => s.id === active);
  const modeName = data.modes.find((m) => m.id === mode)?.name;

  return (
    <div className="max-w-6xl mx-auto" data-testid="test-section-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <h1 className="font-display text-4xl sm:text-5xl font-bold">Test Section</h1>
        <Link to="/app/practice-test/my-passages" className="pm-btn-secondary inline-flex items-center gap-2 text-sm" data-testid="ts-my-passages-link">
          <BookOpenCheck size={15} /> My passages
        </Link>
      </div>
      <p className="text-base mb-8 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
        AAMC-aligned passages for all four MCAT sections. Every science passage has 5–6 validated questions tagged to its foundational concept, content category, topic and subtopic.
      </p>
      <SectionTabs sections={tabs} active={active} onPick={(id) => { setActive(id); setError(null); }} />
      {active === "cars" ? <CarsPanel cars={data.cars} /> : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="space-y-6 min-w-0">
            <p className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="ts-section-blurb">{section.blurb} · {section.bank_count} validated passages in the shared bank.</p>
            <ModeGrid modes={data.modes} active={mode} onPick={(m) => { setMode(m); setError(null); }} />
            <div className="grid md:grid-cols-2 gap-4">
              <Insights sectionId={active} />
              <History sectionId={active} />
            </div>
          </div>
          <div className="lg:sticky lg:top-6">
            <PracticeConfig key={`${active}-${mode}`} section={section} mode={mode} modeName={modeName} passageTypes={data.passage_types}
              cost={data.cost_per_passage} busy={busy} error={error} onStart={start} />
          </div>
        </div>
      )}
      <FullLengthCard />
    </div>
  );
}
